import { Controller } from "@hotwired/stimulus"
import debounce from 'lodash/debounce'

export default class extends Controller {
  static targets = ["searchInput", "searchResults", "profileMenu", "accountName", "accountNameMobile", "teamName", "teamLogo", "accountAvatar"]
  static values = {
    currentUser: Object,
    searchUrl: String
  }

  initialize() {
    // Create debounced search function
    this.debouncedSearch = debounce(this.performSearch.bind(this), 300)
  }

  connect() {
    this.hideSearchResults()
    this.hideProfileMenu()

    // Set search URL if not provided
    if (!this.hasSearchUrlValue) {
      this.searchUrlValue = '/account_searches'
    }

    document.addEventListener("click", (e) => {
      // Don't hide search results if clicking inside the navbar
      if (!this.element.contains(e.target)) {
        this.hideSearchResults()
        this.hideProfileMenu()
      }
    })
  }

  toggleProfileMenu(e) {
    e.stopPropagation() // Prevent event from bubbling up
    this.profileMenuTarget.classList.toggle("hidden")
  }

  search(e) {
    e.preventDefault()
    const query = this.searchInputTarget.value.trim()

    if (query) {
      window.dispatchEvent(new CustomEvent('portalSearch', {
        detail: { query }
      }))
      this.hideSearchResults()
      this.searchInputTarget.value = '' // Clear the search input
    }
  }

  showSearchResults() {
    const query = this.searchInputTarget.value.trim()
    
    if (query.length > 0) {
      this.searchResultsTarget.classList.remove("hidden")
      this.debouncedSearch(query)
    } else {
      this.hideSearchResults()
    }
  }

  async performSearch(query) {
    try {
      // Show loading state
      const resultsContent = this.searchResultsTarget.querySelector('[id="search-results-content"]')
      if (resultsContent) {
        resultsContent.innerHTML = `
          <div class="p-4 text-center text-sm text-gray-500">
            <div class="inline-flex items-center">
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-[#1D31FF]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Searching...
            </div>
          </div>
        `
      }

      // Make request with Turbo
      const response = await fetch(`${this.searchUrlValue}?query=${encodeURIComponent(query)}`, {
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      if (response.ok) {
        const html = await response.text()
        
        // Handle Turbo Stream response manually if Turbo is not available
        if (typeof Turbo !== 'undefined' && Turbo.renderStreamMessage) {
          Turbo.renderStreamMessage(html)
        } else {
          // Manually process the turbo-stream response
          const parser = new DOMParser()
          const doc = parser.parseFromString(html, 'text/html')
          const turboStream = doc.querySelector('turbo-stream')
          
          if (turboStream) {
            const target = turboStream.getAttribute('target')
            const action = turboStream.getAttribute('action')
            const template = turboStream.querySelector('template')
            
            if (action === 'update' && target === 'search-results-content' && template) {
              const resultsContent = this.searchResultsTarget.querySelector(`[id="${target}"]`)
              if (resultsContent) {
                resultsContent.innerHTML = template.innerHTML
              }
            }
          }
        }
      } else {
        throw new Error('Search request failed')
      }
    } catch (error) {
      console.error('Search error:', error)
      const resultsContent = this.searchResultsTarget.querySelector('[id="search-results-content"]')
      if (resultsContent) {
        resultsContent.innerHTML = `
          <div class="p-4 text-center text-sm text-gray-500">
            Error loading results
          </div>
        `
      }
    }
  }

  selectResult(e) {
    const resultElement = e.currentTarget
    const resultId = resultElement.dataset.resultId
    const resultType = resultElement.dataset.resultType
    
    // Dispatch navigation event for React Router to handle
    window.dispatchEvent(new CustomEvent('portalNavigate', {
      detail: { 
        type: resultType,
        id: resultId
      }
    }))
    
    this.hideSearchResults()
    this.searchInputTarget.value = '' // Clear the search input
  }

  viewAllResults() {
    const query = this.searchInputTarget.value.trim()
    if (query) {
      window.dispatchEvent(new CustomEvent('portalSearch', {
        detail: { query }
      }))
      this.hideSearchResults()
      this.searchInputTarget.value = '' // Clear the search input
    }
  }

  hideSearchResults() {
    this.searchResultsTarget.classList.add("hidden")
  }

  hideProfileMenu() {
    this.profileMenuTarget.classList.add("hidden")
  }
}
